import { IAppConfig } from '@estee/elc-universal-utils';
import { observable, action } from 'mobx';

export type PrimitiveValueType = string | number | boolean;

export interface IDropdownOption {
    label: string;
    value: string;
}

// Key: country/area code, or default
// Value: regex pattern
export interface IMobileValidation {
    [key: string]: string;
    default: string;
}

export interface ICustomValidation {
    [key: string]: unknown;
    // Custom validator name registered to FormValidator
    validator: string;
}

export type FieldValueType = 'number' | 'string' | 'boolean';

export interface IFieldValidationsConfig {
    type: FieldValueType;
    validations?: (string | ICustomValidation)[];
    // Defines static options for select input, or valid values for validation only.
    options?: { value: PrimitiveValueType; label?: string }[];
}

export interface IFieldsValidations {
    [key: string]: IFieldValidationsConfig;
}

export interface IUserInfoDisplay {
    name: string;
    email: string;
    mobile: string;
}

// A static value, or string with placeholders or a compound object containing both
export type AdditionalRequestFieldType = string | number | boolean;

export interface IRequestFieldsAdditionalMappings {
    [key: string]: PrimitiveValueType;
}

export type InputType =
    | 'select'
    | 'selectCms'
    | 'text'
    | 'tel'
    | 'label'
    | 'checkboxGroup'
    | 'checkbox'
    | 'dateDay'
    | 'dateMonth'
    | 'dateYear'
    | 'email';

// Validator name and error message mapping
export interface IValidatorMessages {
    [key: string]: string;
}

export interface IFormItem {
    // For text only label, name is also required so that we can customize style easily.
    name: string;
    inputType: InputType;
    labelKey: string;
    x: number;
    y: number;
    errorMessages?: IValidatorMessages;
    // For select or input, string is the only value type allowed.
    initialValue?: string | boolean;
}

export interface ICheckableItem extends IFormItem {
    inverseCheckedValue?: boolean;
    initialValue?: boolean;
    // We use `enabled` instead of `disabled` because eventually this prop will go through to elc-checkbox component.
    // Checkbox is by default enabled if this prop is missing, set it to false to explicity disable it
    enabled?: boolean;
}

export interface ICheckboxGroup extends Omit<IFormItem, 'initialValue'> {
    inverseCheckedValue?: boolean;
    selectAll?: { labelKey: string; name: string };
    items: Omit<ICheckableItem, 'inputType'>[];
}

export function isCheckboxGroup(formItem: InputSchemaType): formItem is ICheckboxGroup {
    return formItem.inputType === 'checkboxGroup' && formItem.hasOwnProperty('items');
}

export function isCmsSelect(formItem: InputSchemaType): formItem is ICmsSelect {
    return formItem.inputType === 'selectCms';
}

interface ISelectSource {
    type: string;
    value: string;
}
export interface ISelect extends IFormItem {
    source?: ISelectSource;
    pin?: string[];
    staticLookupName?: string;
    dependsOnField?: string;
    additionalFormValues?: string[];
}

interface ICmsSelect extends ISelect {
    source: ISelectSource;
}

interface IDaySelect extends ISelect {
    yearFieldName: string;
    monthFieldName: string;
}

interface IDateSelect extends ISelect {
    offsets?: number[];
}

export type InputSchemaType =
    | ICheckboxGroup
    | ICheckableItem
    | ISelect
    | IDateSelect
    | IDaySelect
    | IFormItem;

export interface IFormSection {
    name: string;
    titleLabelKey?: string;
    items: InputSchemaType[];
}

export interface StaticLookupUrls {
    [key: string]: string;
}

export interface IFormSchema {
    sections: IFormSection[];
}

interface IFeatureOptions {
    enabled?: boolean;
    omitHttpMethods: string[];
}

export interface IRampBUConfig {
    commonEmailDomains: string[];
    badCharValidation: string;
    signUpFormInputSchema: IFormSchema;
    fieldsValidations: IFieldsValidations;
    postRequestFields: string[];
    putRequestFields: string[];
    requestFieldsAdditionalMappings: IRequestFieldsAdditionalMappings;
    userInfoDisplay: IUserInfoDisplay;
    staticLookupUrls: StaticLookupUrls;
    features: {
        posFrontiers?: IFeatureOptions;
        updateUserAddress?: IFeatureOptions;
        updateUserLoyalty?: IFeatureOptions;
    };
    identifiers: string[];
}

export interface IRampAppConfig extends IAppConfig {
    // from Drupal/ecommConfig
    pgApiTokens?: {
        rampForm?: string;
    };
}

export interface IRampServiceViewConfig {
    // from data-config
    facebookAppId?: string;
    showLoginForm?: boolean;
}

export interface IRampPreloadedConfig extends IRampAppConfig {
    // from BUID configuration
    rampForm?: IRampBUConfig;
}

export class Config {
    @observable public config: IRampPreloadedConfig = {};

    constructor(config: IAppConfig) {
        this.updateConfig(config);
    }

    @action
    public updateConfig(config: Partial<IRampPreloadedConfig>) {
        this.config = { ...this.config, ...config };
    }
}
